import React, { useState } from 'react';
import { X, Upload, FileSpreadsheet, Download, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import API_URL from '../config';

const ExcelRoomUploadModal = ({ isOpen, onClose, onSuccess, creatorEmail }) => {
    const [roomName, setRoomName] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const uploadedFile = e.dataTransfer.files[0];
            validateAndSetFile(uploadedFile);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            validateAndSetFile(e.target.files[0]);
        }
    };

    const validateAndSetFile = (f) => {
        setError('');
        const ext = f.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls', 'csv'].includes(ext)) {
            setError('Please upload a valid Excel file (.xlsx, .xls) or CSV file.');
            return;
        }
        setFile(f);
    };

    const handleDownloadTemplate = () => {
        window.open(`${API_URL}/formation/tasks/excel-template`, '_blank');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!roomName.trim()) {
            setError('Room Name is required');
            return;
        }
        if (!file) {
            setError('Please select an Excel file to upload');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('name', roomName.trim());
            formData.append('creator_email', creatorEmail);
            if (expiresAt) formData.append('expires_at', expiresAt);
            formData.append('file', file);

            const res = await fetch(`${API_URL}/formation/tasks/from-excel`, {
                method: 'POST',
                body: formData
            });

            const data = await res.json();
            if (res.ok && data.success) {
                alert(`Room "${roomName}" created successfully with ${data.totalTeams} teams and ${data.totalStudents} students!`);
                setRoomName('');
                setExpiresAt('');
                setFile(null);
                onSuccess();
                onClose();
            } else {
                setError(data.error || 'Failed to create room from Excel');
            }
        } catch (err) {
            console.error(err);
            setError('Network error. Failed to upload file.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem',
                    backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    backdropFilter: 'blur(6px)'
                }}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    style={{
                        background: 'var(--glass-bg, #12131C)',
                        border: '1px solid var(--glass-border, rgba(255,255,255,0.15))',
                        borderRadius: '20px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                        width: '100%',
                        maxWidth: '520px',
                        overflow: 'hidden',
                        color: 'var(--text-main, #ffffff)'
                    }}
                >
                    {/* Modal Header */}
                    <div
                        style={{
                            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                            padding: '1.25rem 1.5rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            color: '#ffffff'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div
                                style={{
                                    padding: '10px',
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <FileSpreadsheet size={24} color="#ffffff" />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700' }}>Create Room via Excel</h3>
                                <p style={{ margin: 0, fontSize: '0.8rem', opacity: 0.9 }}>
                                    Upload team roster & assign student access
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#ffffff',
                                cursor: 'pointer',
                                padding: '6px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                opacity: 0.85
                            }}
                            onMouseOver={e => e.currentTarget.style.opacity = '1'}
                            onMouseOut={e => e.currentTarget.style.opacity = '0.85'}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Modal Body / Form */}
                    <form onSubmit={handleSubmit} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        {error && (
                            <div
                                style={{
                                    padding: '0.75rem 1rem',
                                    background: 'rgba(239, 68, 68, 0.15)',
                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    color: '#f87171',
                                    fontSize: '0.85rem'
                                }}
                            >
                                <AlertCircle size={18} style={{ flexShrink: 0 }} />
                                <span>{error}</span>
                            </div>
                        )}

                        {/* Room Name */}
                        <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted, #9ca3af)', marginBottom: '6px' }}>
                                Room / Assignment Name *
                            </label>
                            <input
                                type="text"
                                value={roomName}
                                onChange={(e) => setRoomName(e.target.value)}
                                placeholder="e.g. Major Project Phase 1 - Section A"
                                style={{
                                    width: '100%',
                                    padding: '0.75rem 1rem',
                                    borderRadius: '12px',
                                    border: '1px solid var(--glass-border, rgba(255,255,255,0.15))',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    color: 'var(--text-main, #ffffff)',
                                    fontSize: '0.9rem',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                                required
                            />
                        </div>

                        {/* Expiry Date */}
                        <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted, #9ca3af)', marginBottom: '6px' }}>
                                Expiry / Lock Date (Optional)
                            </label>
                            <input
                                type="datetime-local"
                                value={expiresAt}
                                onChange={(e) => setExpiresAt(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem 1rem',
                                    borderRadius: '12px',
                                    border: '1px solid var(--glass-border, rgba(255,255,255,0.15))',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    color: 'var(--text-main, #ffffff)',
                                    fontSize: '0.9rem',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        {/* Excel File Dropzone */}
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                <label style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-muted, #9ca3af)' }}>
                                    Upload Excel Roster *
                                </label>
                                <button
                                    type="button"
                                    onClick={handleDownloadTemplate}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#818cf8',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        textDecoration: 'underline'
                                    }}
                                >
                                    <Download size={14} /> Download Template
                                </button>
                            </div>

                            <div
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => document.getElementById('excel-file-input').click()}
                                style={{
                                    border: `2px dashed ${dragActive ? '#6366f1' : file ? '#10b981' : 'rgba(255,255,255,0.2)'}`,
                                    borderRadius: '14px',
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    background: dragActive ? 'rgba(99, 102, 241, 0.1)' : file ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255,255,255,0.02)',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <input
                                    id="excel-file-input"
                                    type="file"
                                    accept=".xlsx, .xls, .csv"
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                />

                                {file ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', color: '#34d399' }}>
                                        <CheckCircle2 size={28} />
                                        <div style={{ textAlign: 'left' }}>
                                            <p style={{ margin: 0, fontWeight: '600', fontSize: '0.9rem' }}>{file.name}</p>
                                            <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.7 }}>
                                                {(file.size / 1024).toFixed(1)} KB • Click to replace
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: 'var(--text-muted, #9ca3af)' }}>
                                        <Upload size={32} color="#818cf8" />
                                        <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: 'var(--text-main, #ffffff)' }}>
                                            Click to upload or drag & drop Excel sheet
                                        </p>
                                        <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.7 }}>
                                            Supported formats: .xlsx, .xls, .csv
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Visibility Info Box (Red Alert Box) */}
                        <div
                            style={{
                                padding: '0.9rem 1.1rem',
                                borderRadius: '12px',
                                background: '#ef4444',
                                border: '1px solid #f87171',
                                color: '#ffffff',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                lineHeight: '1.5',
                                boxShadow: '0 4px 14px rgba(239, 68, 68, 0.35)',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px'
                            }}
                        >
                            <AlertCircle size={20} style={{ flexShrink: 0, marginTop: '2px', color: '#ffffff' }} />
                            <span>
                                <strong style={{ color: '#ffffff', textDecoration: 'underline' }}>Access Control:</strong> Only student roll numbers present in this Excel sheet will get access and visibility to this room.
                            </span>
                        </div>

                        {/* Footer Buttons */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '0.5rem' }}>
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={submitting}
                                style={{
                                    padding: '0.65rem 1.25rem',
                                    borderRadius: '12px',
                                    border: '1px solid var(--glass-border, rgba(255,255,255,0.15))',
                                    background: 'transparent',
                                    color: 'var(--text-main, #ffffff)',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '0.85rem'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                style={{
                                    padding: '0.65rem 1.5rem',
                                    borderRadius: '12px',
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                                    color: '#ffffff',
                                    cursor: 'pointer',
                                    fontWeight: '700',
                                    fontSize: '0.85rem',
                                    boxShadow: '0 4px 15px rgba(99,102,241,0.35)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    opacity: submitting ? 0.7 : 1
                                }}
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        <span>Creating...</span>
                                    </>
                                ) : (
                                    <span>Create Room</span>
                                )}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default ExcelRoomUploadModal;
